import { useEffect, useState, useCallback } from "react";
import dayjs from "dayjs";

import { apiCall } from "../../../utils/api";
import { API_URLS } from "../../../constants/api";

// useDueWindow — fetch cấu hình deadline cho 1 năm học (type="student"), trả
// helper `check(startKey, endKey)` để form gọi và biết hiện tại có nằm trong
// cửa sổ thời gian hay không.
//
// BE đã enforce window guard ở service (yearly_due_date_service.go +
// student_research_service.go). Hook này chỉ MIRROR ở FE để disable nút submit
// kèm tooltip lý do — UX preview, không phải authority.
//
// Convention key trùng với field BE: regStartTime/regEndTime,
// reportStartTime/reportEndTime, acceptStartTime/acceptEndTime,
// conferenceStartTime/conferenceEndTime.
export default function useDueWindow(year) {
  const [dueDate, setDueDate] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancel = false;
    const fetchData = async () => {
      const api = API_URLS.YEARLY_DUE_DATE.filterYearlyDueDate();
      const { response, error } = await apiCall({
        ...api,
        payload: { type: "student", schoolYear: year },
      });
      if (cancel) return;
      if (!error && response.status === 200 && response.data.success) {
        setDueDate(response.data.data?.[0] || null);
      }
      setLoaded(true);
    };
    if (year) fetchData();
    else setLoaded(true);
    return () => {
      cancel = true;
    };
  }, [year]);

  // Refactor 2026-05-15: BE cột YearlyDueDate giờ là DATE. So sánh theo NGÀY
  // (không xét giờ phút) — end date INCLUSIVE: hôm nay = end vẫn trong hạn.
  //
  // Lấy date portion từ ISO (substring 0,10) để tránh TZ shift khi browser
  // khác múi VN. Định dạng "YYYY-MM-DD" dayjs parse local timezone OK cho
  // mục đích so sánh date level.
  const datePart = (iso) => {
    if (!iso) return null;
    const s = String(iso).substring(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
  };
  const todayStr = () => dayjs().format("YYYY-MM-DD");

  // check(startKey, endKey) → { isOpen, reason }
  //   - Chưa load → isOpen=true (BE vẫn enforce)
  //   - Cả start+end zero → isOpen=true (chưa cấu hình)
  //   - today_date < start_date → isOpen=false
  //   - today_date > end_date   → isOpen=false (inclusive: today == end OK)
  const check = useCallback(
    (startKey, endKey) => {
      if (!loaded || !dueDate) return { isOpen: true, reason: null };
      const startDate = datePart(dueDate[startKey]);
      const endDate = datePart(dueDate[endKey]);
      if (!startDate && !endDate) return { isOpen: true, reason: null };
      const today = todayStr();
      if (startDate && today < startDate) {
        const [y, m, d] = startDate.split("-");
        return { isOpen: false, reason: `Chưa đến hạn (mở từ ${d}/${m}/${y})` };
      }
      if (endDate && today > endDate) {
        const [y, m, d] = endDate.split("-");
        return { isOpen: false, reason: `Đã quá hạn (đóng ngày ${d}/${m}/${y})` };
      }
      return { isOpen: true, reason: null };
    },
    [loaded, dueDate]
  );

  return { dueDate, loaded, check };
}
